import { useState, type FormEvent } from 'react'
import { CalendarDays, Check, Clock3, Laptop, MapPin, ShieldCheck, Stethoscope, Video } from 'lucide-react'
import { FormStatus } from '../components/FormStatus'
import { PageHero } from '../components/PageHero'
import { PageMeta } from '../components/PageMeta'
import { Reveal } from '../components/Reveal'
import { clinic } from '../data/site'

type VisitType = 'in-person' | 'online'
type Status = 'idle' | 'submitting' | 'success' | 'error'

export default function BookingPage() {
  const [visitType, setVisitType] = useState<VisitType>('in-person')
  const [status, setStatus] = useState<Status>('idle')
  const [message, setMessage] = useState('')

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setStatus('submitting')
    setMessage('Sending your request securely…')
    const form = event.currentTarget
    const payload = Object.fromEntries(new FormData(form).entries())

    try {
      const response = await fetch('/api/appointments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!response.ok) throw new Error('The booking service is not available yet.')
      form.reset()
      setStatus('success')
      setMessage('Request received. This is not confirmed until the clinic replies with a time and location or video link.')
    } catch (error) {
      if (import.meta.env.DEV) {
        setStatus('success')
        setMessage('Form preview complete. No personal data was stored; the secure Cloudflare submission service activates after deployment bindings are configured.')
      } else {
        setStatus('error')
        setMessage(error instanceof Error ? error.message : 'Unable to send. Please try again later.')
      }
    }
  }

  return (
    <>
      <PageMeta title="Book a consultation" description="Request an in-person or online general medical consultation with Dr. Fathima Finu. Final clinic details and availability are confirmed privately." />
      <PageHero
        eyebrow="Consultations"
        title={<>Choose the kind of visit that <span className="serif-accent">fits your day.</span></>}
        copy="Request an in-person or online consultation. This form asks for only the minimum information needed to arrange a time—save clinical details for a private consultation."
        aside={<div className="booking-privacy-card"><ShieldCheck size={26} /><strong>Minimal by design</strong><span>No diagnosis, ID document, or medical attachment is needed to request a visit.</span></div>}
      />

      <section className="section booking-section">
        <div className="container booking-layout">
          <Reveal className="booking-info">
            <span className="eyebrow">Visit information</span>
            <h2>A request first, then a personal confirmation.</h2>
            <p>Availability can change with clinic schedules and urgent clinical work. You will receive the final time—and the clinic address or secure video details—only after the request is accepted.</p>
            <div className="visit-detail-card">
              <MapPin size={21} />
              <div><strong>{clinic.name}</strong><span>{clinic.address}</span></div>
            </div>
            <div className="visit-detail-card">
              <Clock3 size={21} />
              <div><strong>Current consultation hours</strong>{clinic.hours.map((slot) => <span key={slot.day}>{slot.day}: {slot.time}</span>)}</div>
            </div>
            <div className="availability-note"><CalendarDays size={19} /><span>{clinic.notice}</span></div>
            <div className="not-for-emergency"><strong>Need urgent help?</strong><p>Do not use this form for emergencies or time-sensitive symptoms. Contact your local emergency service or go to the nearest emergency department.</p></div>
          </Reveal>

          <Reveal className="booking-form-panel" delay={0.08}>
            <div className="visit-tabs" role="tablist" aria-label="Consultation type">
              <button type="button" role="tab" aria-selected={visitType === 'in-person'} className={visitType === 'in-person' ? 'is-active' : ''} onClick={() => setVisitType('in-person')}><Stethoscope size={19} /> In person</button>
              <button type="button" role="tab" aria-selected={visitType === 'online'} className={visitType === 'online' ? 'is-active' : ''} onClick={() => setVisitType('online')}><Video size={19} /> Online</button>
            </div>
            <div className="visit-type-summary">
              {visitType === 'in-person' ? <><MapPin size={20} /><span><strong>Clinic consultation</strong>Location shared after confirmation.</span></> : <><Laptop size={20} /><span><strong>Secure video consultation</strong>Eligibility may depend on location and clinical need.</span></>}
            </div>
            <form className="booking-form" onSubmit={handleSubmit}>
              <input type="hidden" name="visitType" value={visitType} />
              <div className="field-row">
                <label><span>Full name</span><input name="name" autoComplete="name" required maxLength={100} placeholder="Your name" /></label>
                <label><span>Reply email</span><input name="email" type="email" autoComplete="email" required maxLength={180} placeholder="you@example.com" /></label>
              </div>
              <div className="field-row">
                <label><span>Preferred date</span><input name="preferredDate" type="date" required min={new Date().toISOString().slice(0, 10)} /></label>
                <label><span>Preferred time</span><select name="preferredTime" required defaultValue=""><option value="" disabled>Select a window</option><option>Morning</option><option>Afternoon</option><option>Evening</option></select></label>
              </div>
              <label><span>Short reason for the visit <small>(optional)</small></span><textarea name="reason" rows={4} maxLength={500} placeholder="For example: routine review or a new non-urgent concern. Please do not include test reports, ID numbers, or detailed history here." /></label>
              <label className="checkbox-field"><input type="checkbox" name="privacyConsent" value="yes" required /><span><i><Check size={13} /></i>I understand this is a request, not a confirmed appointment, and agree to be contacted about scheduling.</span></label>
              <button className="button form-submit" type="submit" disabled={status === 'submitting'}>Request this visit <CalendarDays size={18} /></button>
              <FormStatus status={status} message={message} />
              <p className="form-fine-print">Submitted details are used only to arrange the requested consultation. See <a href="/privacy">privacy & safety</a>.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  )
}
